import Label from '@/components/Label'
import clsx from 'clsx'
import { setDefaultOptions } from 'date-fns'
import { ro } from 'date-fns/locale'
import { isValidCNP } from '@/lib/cnp'

setDefaultOptions({ locale: ro })

export default function DynamicForm({
  fields,
  register,
  profile,
  errors,
  setValue,
}) {
  // Function to extract and format date from CNP
  const extractDateFromCNP = (cnp) => {
    if (!cnp || cnp.length < 6) return ''

    const year = cnp.substring(1, 3)
    const month = cnp.substring(3, 5)
    const day = cnp.substring(5, 7)

    // Determine century based on first digit
    const firstDigit = cnp.charAt(0)
    let fullYear

    if (firstDigit === '1' || firstDigit === '2') {
      fullYear = '19' + year
    } else if (firstDigit === '5' || firstDigit === '6') {
      fullYear = '20' + year
    } else {
      return '' // Invalid or unsupported CNP format
    }

    return `${day}.${month}.${fullYear}`
  }

  // Function to handle CNP change and auto-fill birth date
  const handleCNPChange = (cnp, fieldName = null) => {
    if (cnp && cnp.length === 13 && /^\d{13}$/.test(cnp)) {
      if (isValidCNP(cnp)) {
        const extractedDate = extractDateFromCNP(cnp)
        if (extractedDate) {
          // Look for date fields that might be birth dates
          const dateFields = fields.filter(
            (f) => f.form_field_id.type === 'date'
          )

          // Find birth date fields to auto-fill
          const birthDateFields = dateFields.filter(
            (f) =>
              f.form_field_id.name.toLowerCase().includes('nastere') ||
              f.form_field_id.name.toLowerCase().includes('data_nasterii') ||
              f.form_field_id.name.toLowerCase().includes('birth') ||
              f.form_field_id.title?.toLowerCase().includes('naștere') ||
              f.form_field_id.title?.toLowerCase().includes('birth')
          )

          // If the CNP field name gives us context about whose CNP it is,
          // we can be more specific about which date field to fill
          if (fieldName) {
            const fieldNameLower = fieldName.toLowerCase()

            // If it's a general CNP field or person's CNP, fill all birth date fields
            if (
              fieldNameLower.includes('cnp') ||
              fieldNameLower.includes('personal')
            ) {
              birthDateFields.forEach((field) => {
                setValue(field.form_field_id.name, extractedDate)
              })
            }
          } else {
            // Default behavior: fill the first birth date field found
            if (birthDateFields.length > 0) {
              setValue(birthDateFields[0].form_field_id.name, extractedDate)
            }
          }
        }
      }
    }
  }

  return (
    <>
      {fields.map((field) => (
        <div
          className={clsx(
            'mt-4',
            field.form_field_id.width == '100' && 'md:col-span-2'
          )}
          key={field.form_field_id.name}
        >
          <Label
            text={field.form_field_id.title || field.form_field_id.name}
            forId={field.form_field_id.name}
            required={field.form_field_id.options?.includes('required')}
          />
          {field.form_field_id.type === 'date' ? (
            <input
              type="text"
              id={field.form_field_id.name}
              placeholder="zz.ll.aaaa"
              defaultValue={
                field.form_field_id.default_value ||
                profile?.[field.form_field_id.name]
              }
              {...register(field.form_field_id.name, {
                required: field.form_field_id.options?.includes('required'),
              })}
              className={clsx(
                'group block w-full px-2 py-2 mt-1 text-lg border font-medium rounded-none focus:outline-none focus:ring-4 focus:ring-primary-light focus:border-primary-dark placeholder-normal',
                errors[field.form_field_id.name]
                  ? 'border-red-500 border-2'
                  : 'border-slate-300'
              )}
            />
          ) : (
            <input
              type="text"
              id={field.form_field_id.name}
              defaultValue={
                field.form_field_id.default_value ||
                profile?.[field.form_field_id.name]
              }
              {...register(field.form_field_id.name, {
                validate: (v) => {
                  let result =
                    !field.form_field_id.options?.includes('required') ||
                    v.length > 0 ||
                    v == true

                  if (field.form_field_id.type == 'cnp') {
                    if (v.length == 0) return 'CNP incomplet'
                    if (v.length !== 13) return 'CNP trebuie să aibă 13 cifre'
                    if (!/^\d{13}$/.test(v))
                      return 'CNP trebuie să conțină doar cifre'

                    if (!isValidCNP(v)) {
                      return 'CNP invalid'
                    }
                  }

                  return result
                },
                onChange:
                  field.form_field_id.type === 'cnp'
                    ? (e) =>
                        handleCNPChange(
                          e.target.value,
                          field.form_field_id.name
                        )
                    : undefined,
              })}
              className={clsx(
                'group block w-full px-2 py-2 mt-1 text-lg border font-medium rounded-none focus:outline-none focus:ring-4 focus:ring-primary-light focus:border-primary-dark placeholder-normal',
                errors[field.form_field_id.name]
                  ? 'border-red-500 border-2'
                  : 'border-slate-300'
              )}
            />
          )}
          {errors[field.form_field_id.name] && (
            <div className="text-red-500 py-1">
              {errors[field.form_field_id.name].message as string}
            </div>
          )}
        </div>
      ))}
    </>
  )
}
